import { faHand } from "@jewlr/pro-light-svg-icons/faHand"
import { faChevronDown } from "@jewlr/pro-regular-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-regular-svg-icons/faChevronUp"
import { faCheckCircle as solidCheckCircle } from "@jewlr/pro-solid-svg-icons/faCheckCircle"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useState } from "react"
import { connect } from "react-redux"

import { addAddon, removeAddon } from "areas/cart/store/actions"
import Button from "components/ui/button"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { freeGiftPromoEnabled } from "helpers/holiday"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

import { AddonItem, AddonSlider } from "./components"

const AddonButton = ({ className = "", selected, ...props }) => (
  <Button
    className={cn(
      "focus-visible:border-selected focus-visible:outline-hidden relative rounded-md border-2 border-transparent",
      selected && "border-selected border-2",
      className
    )}
    style={{ width: "130px" }}
    {...props}
  />
)
const AddonIcon = ({ ...props }) => (
  <div
    className={cn(
      "text-selected text-22 size-22 absolute -bottom-10 left-1/2 -translate-x-1/2 rounded-full bg-white leading-none"
    )}
    {...props}
  />
)
const FreeGiftItem = ({ gift, handleClick, selected, ...props }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <AddonButton
      aria-checked={selected}
      onClick={handleClick}
      role="radio"
      selected={selected}
      {...keyboardProps}
      {...props}
    >
      <Image
        alt={gift.title}
        className="rounded-md"
        height="126"
        src={gift.image_url}
        style={{ width: "130px" }}
        width="126"
      />
      {selected && (
        <AddonIcon>
          <FontAwesomeIcon icon={solidCheckCircle} />
        </AddonIcon>
      )}
    </AddonButton>
  )
}

FreeGiftItem.propTypes = {
  gift: PropTypes.object,
  handleClick: PropTypes.func,
  selected: PropTypes.bool,
}

const NoGiftItem = ({ handleClick, selected, ...props }) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <AddonButton
      aria-checked={selected}
      className="bg-[#f8f8f8]"
      onClick={handleClick}
      role="radio"
      selected={selected}
      {...keyboardProps}
      {...props}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{ height: "130px", width: "130px" }}
      >
        <FontAwesomeIcon className="text-grey-30 text-20 mb-6" icon={faHand} />
        <p className="text-14 leading-18 tracking-tight">No Thanks</p>
      </div>
      {selected && (
        <AddonIcon>
          <FontAwesomeIcon icon={solidCheckCircle} />
        </AddonIcon>
      )}
    </AddonButton>
  )
}

NoGiftItem.propTypes = {
  handleClick: PropTypes.func,
  selected: PropTypes.bool,
}

const InternalFreeGifts = ({
  chooseGift,
  freeGifts,
  removeGift,
  selectedFreeGift,
}) => {
  if (!freeGifts?.length) return null

  const [selectedGift, setGift] = useState(selectedFreeGift || freeGifts[0])
  const [showGiftDetails, toggleGiftDetails] = useState(false)

  return (
    <Media lessThan="tablet">
      <div
        className={cn(
          "border-grey-20 py-18 mt-24 rounded-xl border bg-white px-12 text-center shadow-lg",
          freeGiftPromoEnabled() && "mt-16"
        )}
      >
        <h2 className="text-22 desktop:text-34 mb-16 font-bold">
          Choose your FREE Gift
        </h2>
        <FocusScope>
          <AddonSlider
            aria-label="Choose a free gift"
            data-cy="cart-freegift-category-mobile"
            justifyContent="flex-start"
            role="radiogroup"
          >
            {freeGifts.map((gift, i) => {
              const selected =
                (selectedFreeGift?.addon_code ||
                  selectedFreeGift?.gift_code) === gift.gift_code
              return (
                <AddonItem
                  className="mb-18"
                  data-cy={`cart-freegift-mobile-${gift.gift_code}`}
                  key={`freegift-${gift.gift_code}`}
                >
                  <FreeGiftItem
                    gift={gift}
                    handleClick={() => {
                      setGift(gift)
                      chooseGift(gift.gift_code)
                    }}
                    selected={selected}
                    tabIndex={
                      selected || (!selectedFreeGift && i === 0) ? 0 : -1
                    }
                  />
                </AddonItem>
              )
            })}
            <AddonItem className="mb-18" data-cy="cart-freegift-mobile-none">
              <NoGiftItem
                handleClick={() => removeGift()}
                selected={!selectedFreeGift}
                tabIndex={!selectedFreeGift ? 0 : -1}
              />
            </AddonItem>
          </AddonSlider>
        </FocusScope>
        <h3
          className="body mb-8 font-semibold"
          data-cy="cart-freegift-title-mobile"
        >
          {selectedGift.title}
        </h3>
        <div className="flex items-center justify-center gap-6">
          <h5 className="text-green-darker text-30 font-medium">FREE</h5>
          <div className="text-grey-62 text-12 leading-12 text-left font-medium tracking-wide">
            {freeGiftPromoEnabled() ? (
              <>
                {sl.config.free_gift_promo?.retail_value && (
                  <>
                    <p>{to_currency(sl.config.free_gift_promo.retail_value)}</p>
                    <p>VALUE</p>
                  </>
                )}
              </>
            ) : (
              <>
                <span>RETAIL</span>
                <strike className="block">
                  {to_currency(selectedGift.retail_price)}
                </strike>
              </>
            )}
          </div>
        </div>
        <Button
          aria-controls="cart-free-gift-mobile-details"
          aria-expanded={showGiftDetails}
          className="mt-8"
          onClick={() => toggleGiftDetails(!showGiftDetails)}
          text
          variant="secondary"
        >
          <p className="body-sm font-semibold">View Item Details</p>
          <FontAwesomeIcon
            className="text-8 ml-4"
            icon={showGiftDetails ? faChevronUp : faChevronDown}
          />
        </Button>
        <Expand
          className="mt-8"
          id="cart-free-gift-mobile-details"
          show={showGiftDetails}
        >
          <p className="text-12 leading-14 text-left font-medium">
            {selectedGift.description}
          </p>
          {freeGiftPromoEnabled() && (
            <>
              <br />
              <p className="text-10 leading-12 text-left italic">
                While supplies last.
              </p>
            </>
          )}
        </Expand>
      </div>
    </Media>
  )
}

InternalFreeGifts.propTypes = {
  chooseGift: PropTypes.func,
  freeGifts: PropTypes.array,
  removeGift: PropTypes.func,
  selectedFreeGift: PropTypes.object,
}

AddonButton.propTypes = {
  className: PropTypes.string,
  selected: PropTypes.bool,
}
const mapStateToProps = (state) => {
  return {
    freeGifts: state.cart.free_gift_offers,
    selectedFreeGift: state.cart.addons.find(
      (addon) => addon.addon_type == "GIFT"
    ),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    chooseGift: (addonCode) => {
      // Mobile Cart select gift
      dispatch(addAddon(addonCode, "GIFT", "cart_mobile"))
    },
    dispatch,
  }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { selectedFreeGift } = stateProps
  const { dispatch } = dispatchProps

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    removeGift: () => {
      if (selectedFreeGift) {
        dispatch(removeAddon(selectedFreeGift.id))
      }
    },
  }
}

const MobileFreeGifts = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalFreeGifts)

export default MobileFreeGifts
